import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { withStyles } from 'tss-react/mui';
import { isValid, format } from 'date-fns';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';

import {
    saveReportSectionData,
    uploadReportAttachments,
    patchReportVersionSuccess,
    patchReportVersion,
    patchReportError,
    updateSectionProgress,
    REQUEST_STATUS,
    determineSectionsProgress,
    getFlaggedVersion,
} from '../../../redux/reports'; // flattenQuestions
import { getSectionByIdSelector } from '../../../redux/current-report/selectors';
import { getGetReportAttachmentsUploadStatus } from '../../../redux/reports/selectors';
import getValidation from '../../../validation/validation-schema-report';

import ReportSectionHeader from '../../../routes/report/components/section/header';
import FormButtons from '../../../routes/report/components/section/form-buttons';
import ReportSection from '../../../routes/report/components/section';
import AbandonChangesModal from '../../../components/abandon-changes-modal';

import ReportHeader from '../components/header';
import ReportError from '../components/report-error';
import { getRole } from '../../../redux/auth/selectors';
import Styles from './styles';
import { APIError } from '../../../redux/app';
import { SECTION_PROGRESS } from '../../../redux/current-report/constants';
import { getFormattedDate } from '../../../utils/get-formatted-date';
import { USER_ROLES } from '../../../utils/constants';
// import { current } from '@reduxjs/toolkit';

const debounce = (func, wait, immediate) => {
    let timeout;

    return (...args) => {
        const context = this;

        const later = () => {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);

        timeout = setTimeout(later, wait);

        if (callNow) func.apply(context, args);
    };
};

const useYupValidationResolver = (validationSchema) => async (data) => {
    if (!validationSchema || !Object.keys(validationSchema).length) {
        return {
            values: data,
            errors: {},
        };
    }

    try {
        const values = await validationSchema.validate(data, {
            abortEarly: false,
        });

        return {
            values,
            errors: {},
        };
    } catch (errors) {
        return {
            values: {},
            errors: errors?.inner?.reduce(
                (allErrors, currentError) => ({
                    ...allErrors,
                    [currentError.path]: {
                        type: currentError.type ?? 'validation',
                        message: currentError.message,
                    },
                }),
                {},
            ),
        };
    }
};

const ReportSectionLayout = ({ classes, currentReport, flaggedVersion }) => {
    // const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id: reportId } = currentReport;
    const { siteName } = currentReport.testSite;
    const { sectionId } = useParams();
    const getSectionById = useSelector(getSectionByIdSelector);
    const currentSection = getSectionById(sectionId);
    const userRole = useSelector(getRole);
    const reportAttachmentsUploadStatus = useSelector(getGetReportAttachmentsUploadStatus);
    const [uploadedAttachments, setUploadedAttachments] = useState([null]);

    const defaultValues = {};
    // look to the answers object which has the correct structure
    // NOTE: that this will bring in ALL answers for the report, not just the ones for this section
    // when we validate the form, we will only validate the questions for this section
    Object.entries(currentReport.reportAnswers).forEach((question) => {
        // question[0] = key
        // question[1] = value
        defaultValues[question[0]] = question[1] || null;
    });

    const [validationSchema, setValidationSchema] = useState({});
    const [formData, setFormData] = useState({});
    const getSchema = (data) => {
        setValidationSchema(getValidation(currentSection.questions, data));
    };

    // Initialize validation schema on mount
    useEffect(() => {
        getSchema(defaultValues);
    }, [currentSection.questions]); // Re-run if questions change

    const resolver = useMemo(() => useYupValidationResolver(validationSchema), [validationSchema]);
    const {
        handleSubmit,
        control,
        register,
        formState: { errors },
        setValue,
        getValues,
        watch,
    } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues,
        resolver,
    });
    const watchValues = watch();

    // Update validation schema immediately for critical changes, with debounce for performance
    const updateValidationSchema = useCallback((data) => {
        getSchema(data);
    }, []);

    const debouncedUpdateValidationSchema = useCallback(
        debounce((data) => {
            updateValidationSchema(data);
        }, 100),
        [updateValidationSchema],
    );

    // Recalculate section progress when answers change
    const updateSectionProgressBasedOnAnswers = useCallback(
        debounce(() => {
            const updatedAnswers = { ...currentReport.reportAnswers, ...watchValues };
            const flaggedAnswers = getFlaggedVersion(currentReport)?.flaggedAnswers;
            const newSectionsProgress = determineSectionsProgress(
                updatedAnswers,
                flaggedAnswers,
                currentReport.test.sections,
            );
            const currentSectionProgress = newSectionsProgress[currentSection.id];

            // Only update if the calculated progress differs from the stored progress
            if (currentReport.sectionsProgress[currentSection.id] !== currentSectionProgress) {
                dispatch(updateSectionProgress(currentReport.id, currentSection.id, currentSectionProgress));
            }
        }, 1000),
        [currentReport, currentSection, watchValues, dispatch],
    );

    // Check if a value change affects conditional questions visibility
    const affectsConditionalQuestions = useCallback(
        (oldValues, newValues) => {
            // Find questions that have child questions with renderLogic
            const questionsWithChildren = currentSection.questions.filter((q) => q.questions && q.questions.length > 0);

            return questionsWithChildren.some((question) => {
                const oldValue = oldValues[question.id];
                const newValue = newValues[question.id];
                return oldValue !== newValue;
            });
        },
        [currentSection.questions],
    );

    // Calculate if all required questions are actually answered (more reliable than form isValid)
    const isFormCompletelyValid = useCallback(
        (currentValues) => {
            const combinedAnswers = { ...currentReport.reportAnswers, ...currentValues };

            // Check section progress without flaggedAnswers to see if all questions are answered
            const sectionsProgressWithoutFlags = determineSectionsProgress(combinedAnswers, null, [currentSection]);
            return sectionsProgressWithoutFlags[currentSection.id] === SECTION_PROGRESS.COMPLETED;
        },
        [currentReport, currentSection],
    );

    useEffect(() => {
        if (!isEqual(watchValues, formData)) {
            const previousFormData = formData;
            setFormData(watchValues);

            // If this change affects conditional questions, update validation schema immediately
            if (affectsConditionalQuestions(previousFormData, watchValues)) {
                updateValidationSchema(watchValues);
            } else {
                // Otherwise use debounced update for performance
                debouncedUpdateValidationSchema(watchValues);
            }

            updateSectionProgressBasedOnAnswers();
        }
    }, [
        watchValues,
        formData,
        affectsConditionalQuestions,
        updateValidationSchema,
        debouncedUpdateValidationSchema,
        updateSectionProgressBasedOnAnswers,
    ]);

    const handleDateChange = useCallback(
        (date, name) => {
            if (isValid(date)) {
                setValue(name, getFormattedDate(date), { shouldValidate: true, shouldDirty: true });
            } else if (date === null) setValue(name, null, { shouldValidate: true, shouldDirty: true });
        },
        [setValue],
    );

    const handleTimeChange = useCallback(
        (time, name) => {
            if (time && isValid(time)) {
                setValue(name, format(time, 'hh:mm aa'), { shouldValidate: true, shouldDirty: true });
            } else if (time === null) {
                setValue(name, null, { shouldValidate: true, shouldDirty: true });
            }
        },
        [setValue],
    );

    const onError = (error) => {
        // TODO: validate error object structure
        if (error.status === 400) {
            dispatch(APIError({ text: 'Error Saving reports' }));
        } else if (error.status === 422) {
            dispatch(
                APIError({
                    text: 'Error: you appear to be offline, dont navigate away or you will lose your entered data. Try saving once you have connectivity.',
                }),
            );
        }
    };

    const onComplete = () => {
        navigate('/report');
    };

    const saveAttachments = async () => {
        const validAttachments = uploadedAttachments.filter((attachment) => attachment !== null);

        if (validAttachments.length !== 0) {
            await dispatch(
                uploadReportAttachments({
                    reportId,
                    attachments: validAttachments,
                    questions: currentSection.questions,
                    onError,
                }),
            );
        }
    };

    const getBaseQuestionId = (questionId) => {
        const parts = questionId.split('-');
        return parts.length > 5 ? parts.slice(0, 5).join('-') : questionId;
    };

    const clearSectionFlags = async () => {
        const currentFlaggedAnswers = flaggedVersion.flaggedAnswers || {};
        const sectionQuestionIds = new Set();

        const collectQuestionIds = (questionList) => {
            questionList.forEach((q) => {
                sectionQuestionIds.add(q.id);
                if (q.questions) {
                    collectQuestionIds(q.questions);
                }
            });
        };
        collectQuestionIds(currentSection.questions);

        const updatedFlaggedAnswers = Object.keys(currentFlaggedAnswers).reduce((acc, questionId) => {
            const baseQuestionId = getBaseQuestionId(questionId);
            if (!sectionQuestionIds.has(baseQuestionId)) {
                acc[questionId] = currentFlaggedAnswers[questionId];
            }
            return acc;
        }, {});

        if (Object.keys(currentFlaggedAnswers).length !== Object.keys(updatedFlaggedAnswers).length) {
            return new Promise((resolve, reject) => {
                const onSuccess = () => {
                    dispatch(
                        patchReportVersionSuccess(reportId, flaggedVersion.id, {
                            flaggedAnswers: updatedFlaggedAnswers,
                        }),
                    );
                    resolve();
                };

                const onErrorPatch = (error) => {
                    reject(error);
                };

                dispatch(
                    patchReportVersion({
                        versionId: flaggedVersion.id,
                        payload: { comment: null, flaggedAnswers: updatedFlaggedAnswers },
                        onSuccess,
                        onError: onErrorPatch,
                    }),
                );
            });
        }

        return Promise.resolve();
    };

    const onFormSubmit = async () => {
        await saveAttachments();

        const answers = watchValues;
        const { questions } = currentSection;

        await dispatch(
            saveReportSectionData({
                status: 'completed',
                reportId,
                sectionId,
                questions,
                answers,
                onComplete: () => {},
                onError,
            }),
        );

        await clearSectionFlags();
        dispatch(updateSectionProgress(currentReport.id, currentSection.id, SECTION_PROGRESS.COMPLETED));
        onComplete();
    };

    const onSaveDraft = async () => {
        await saveAttachments();

        const { questions } = currentSection;

        await dispatch(
            saveReportSectionData({
                status: 'draft',
                reportId,
                sectionId,
                questions,
                answers: getValues(),
                onComplete: () => {},
                onError,
            }),
        );

        await clearSectionFlags();

        const updatedAnswers = { ...currentReport.reportAnswers, ...getValues() };
        const newSectionsProgress = determineSectionsProgress(updatedAnswers, {}, currentReport.test.sections);
        const newSectionProgress = newSectionsProgress[currentSection.id];

        if (currentReport.sectionsProgress[currentSection.id] !== newSectionProgress) {
            dispatch(updateSectionProgress(currentReport.id, currentSection.id, newSectionProgress));
        }

        onComplete();
    };

    const [abandonFormModalOpen, setAbandonFormModalOpen] = useState(false);
    const onCancelFeedback = () => {
        setAbandonFormModalOpen(true);
    };
    const handleConfirmCancelFeedback = () => {
        // feedback is only stored in the redux store, so if we navigate back it will reset
        navigate('/report');
    };

    const onSaveFeedback = () => {
        // add error messages if any flaggedAnswers are missing a reason
        const flaggedAnswers = flaggedVersion.flaggedAnswers || {};
        let hasErrors = false;
        const validatedAnswers = Object.entries(flaggedAnswers).reduce((acc, [questionId, data]) => {
            if (!data.reason) {
                hasErrors = true;
                acc[questionId] = { ...data, error: 'Reason is required' };
            } else {
                const { error, ...validatedData } = data;
                acc[questionId] = validatedData;
            }
            return acc;
        }, {});
        const { id, ...payload } = flaggedVersion;
        if (hasErrors) {
            dispatch(patchReportVersionSuccess(reportId, id, { ...flaggedVersion, flaggedAnswers: validatedAnswers }));
        } else {
            const onSuccess = () => {
                dispatch(
                    patchReportVersionSuccess(reportId, id, { ...flaggedVersion, flaggedAnswers: validatedAnswers }),
                );

                let sectionContainsFlaggedAnswers = false;
                Object.values(currentSection.questions).forEach((question) => {
                    if (validatedAnswers[question.id]) {
                        sectionContainsFlaggedAnswers = true;
                    }
                });
                if (sectionContainsFlaggedAnswers) {
                    // if flagged answers are present, set the section to flagged
                    dispatch(updateSectionProgress(currentReport.id, currentSection.id, SECTION_PROGRESS.FLAGGED));
                } else {
                    // if no flagged answers are present, set the section to completed
                    dispatch(updateSectionProgress(currentReport.id, currentSection.id, SECTION_PROGRESS.COMPLETED));
                }

                onComplete();
            };
            const onErrorPatch = (error) => {
                if (error?.response?.status === 401) {
                    dispatch(
                        patchReportError(
                            reportId,
                            {
                                message:
                                    'Unable to save your changes. Your session may have expired, if the problem persists please log out and back in again.',
                            },
                            REQUEST_STATUS.SESSION_EXPIRED,
                        ),
                    );
                } else {
                    dispatch(
                        patchReportError(reportId, { message: 'Unable to save your changes.' }, REQUEST_STATUS.ERROR),
                    );
                }
            };
            dispatch(
                patchReportVersion({
                    versionId: id,
                    payload: { ...payload, flaggedAnswers: validatedAnswers, sectionId },
                    onSuccess,
                    onError: onErrorPatch,
                }),
            );
        }
    };

    const onBackButtonClick = () => {
        // if the user is a tester, we want to save the draft when they navigate back
        if (userRole === USER_ROLES.tester.value) {
            onSaveDraft();
        } else {
            // if the user is not a tester, we assume they are a reviewing admin, we want to cancel the feedback
            onCancelFeedback();
        }
    };

    const sectionProps = {
        id: currentReport.id,
        instructions: currentSection.instructions,
        questions: currentSection.questions,
        onSaveDraft,
        control,
        register,
        watchValues,
        errors,
        flaggedAnswers: flaggedVersion.flaggedAnswers || {},
        handleDateChange,
        handleTimeChange,
        setUploadedAttachments,
        setValue,
    };

    const [progressBlockerOpen, setProgressBlockerOpen] = useState(false);
    const handleCloseProgressBlocker = () => {
        setProgressBlockerOpen(false);
    };

    useEffect(() => {
        if (reportAttachmentsUploadStatus?.inProgress) {
            setProgressBlockerOpen(true);
        } else if (!reportAttachmentsUploadStatus?.inProgress) {
            handleCloseProgressBlocker();
        }
    }, [reportAttachmentsUploadStatus]);

    return (
        <>
            <ReportHeader backButtonLabel={`Report: ${siteName}`} onBackButtonClick={onBackButtonClick} />
            <main>
                <AbandonChangesModal
                    open={abandonFormModalOpen}
                    handleClose={() => setAbandonFormModalOpen(false)}
                    handleConfirm={handleConfirmCancelFeedback}
                />
                <Container maxWidth="lg">
                    <Grid item container spacing={2} className={classes.container}>
                        <Grid item xs={4}>
                            <ReportSectionHeader section={currentSection} />
                        </Grid>
                        <Grid item xs={8}>
                            <form noValidate>
                                <Backdrop
                                    className={classes.backdrop}
                                    open={progressBlockerOpen}
                                    onClick={handleCloseProgressBlocker}
                                >
                                    <CircularProgress color="inherit" style={{ marginBottom: 30 }} />
                                    <Typography variant="h3" style={{ color: 'white' }}>
                                        Uploading attachments...
                                    </Typography>
                                </Backdrop>

                                <ReportSection {...sectionProps} />

                                <ReportError currentReport={currentReport} />

                                <FormButtons
                                    onSaveDraft={onSaveDraft}
                                    onSubmit={handleSubmit(onFormSubmit)}
                                    onSaveFeedback={onSaveFeedback}
                                    onCancelFeedback={onCancelFeedback}
                                    role={userRole}
                                    isFormValid={isFormCompletelyValid(watchValues)}
                                />
                            </form>
                        </Grid>
                    </Grid>
                </Container>
            </main>
        </>
    );
};

ReportSectionLayout.propTypes = {
    classes: PropTypes.object.isRequired,
    currentReport: PropTypes.object.isRequired,
    flaggedVersion: PropTypes.object.isRequired,
};

export default withStyles(ReportSectionLayout, Styles);
